import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/quota");
      setQuota(data);
    } catch (e) {
      // ignore
    }
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("pbp_token");
    const savedUser = localStorage.getItem("pbp_user");
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      await refreshUser();
    } else {
      // SSO cookie session (from Job Basket /sso accept) — no local token needed.
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        localStorage.setItem("pbp_user", JSON.stringify(data.user));
        await refreshUser();
      } catch (e) {
        // not signed in via cookie either
      }
    }
    setLoading(false);
  }, [refreshUser]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(
    async (token, userObj) => {
      localStorage.setItem("pbp_token", token);
      localStorage.setItem("pbp_user", JSON.stringify(userObj));
      setUser(userObj);
      await refreshUser();
    },
    [refreshUser]
  );

  const logout = useCallback(() => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("pbp_token");
    localStorage.removeItem("pbp_user");
    setUser(null);
    setQuota(null);
  }, []);

  const value = {
    user,
    quota,
    loading,
    login,
    logout,
    refreshUser,
    upgradeOpen,
    openUpgrade: () => setUpgradeOpen(true),
    closeUpgrade: () => setUpgradeOpen(false),
    setQuota,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
